import React, { useMemo } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts'
import { computeMultiplier, addDays, formatShortDate } from '@/lib/ramp-math'

interface RampPreviewChartProps {
  startDate: string
  endDate: string
  rampUpDays: number
  rampDownDays: number
  rampCurve: 'linear' | 'ease_in'
  peakMultiplier: number
}

export default function RampPreviewChart({
  startDate,
  endDate,
  rampUpDays,
  rampDownDays,
  rampCurve,
  peakMultiplier,
}: RampPreviewChartProps) {
  const data = useMemo(() => {
    const points: { date: string; multiplier: number; zone: string }[] = []
    const start = new Date(startDate + 'T00:00:00')
    const end = new Date(endDate + 'T00:00:00')

    for (let i = rampUpDays; i >= 1; i--) {
      const d = addDays(startDate, -i)
      const dayIndex = rampUpDays - i + 1
      const progress = dayIndex / (rampUpDays + 1)
      points.push({
        date: formatShortDate(d),
        multiplier: Math.round(computeMultiplier(peakMultiplier, progress, rampCurve) * 1000) / 1000,
        zone: 'ramp_up',
      })
    }

    const peakDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
    for (let i = 0; i < peakDays; i++) {
      const d = addDays(startDate, i)
      points.push({
        date: formatShortDate(d),
        multiplier: peakMultiplier,
        zone: 'peak',
      })
    }

    for (let i = 1; i <= rampDownDays; i++) {
      const d = addDays(endDate, i)
      const dayIndex = rampDownDays - i + 1
      const progress = dayIndex / (rampDownDays + 1)
      points.push({
        date: formatShortDate(d),
        multiplier: Math.round(computeMultiplier(peakMultiplier, progress, rampCurve) * 1000) / 1000,
        zone: 'ramp_down',
      })
    }

    return points
  }, [startDate, endDate, rampUpDays, rampDownDays, rampCurve, peakMultiplier])

  if (data.length === 0) return null

  return (
    <ResponsiveContainer width="100%" height={140}>
      <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
        <defs>
          <linearGradient id="rampFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#0d9488" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#0d9488" stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 10, fill: '#64748b' }}
          tickLine={false}
          axisLine={{ stroke: '#e2e8f0' }}
        />
        <YAxis
          domain={[1, 'auto']}
          tick={{ fontSize: 10, fill: '#64748b' }}
          tickFormatter={(v: number) => `${v}x`}
          tickLine={false}
          axisLine={false}
        />
        <ReferenceLine y={1} stroke="#94a3b8" strokeDasharray="4 4" label={{ value: 'Normal', fontSize: 9, fill: '#94a3b8' }} />
        <Tooltip
          formatter={(value: number | undefined) => [`${value ?? 0}x (${(((value ?? 0) - 1) * 100).toFixed(1)}%)`, 'Multiplier']}
          labelStyle={{ fontSize: 11, color: '#0f172a' }}
          contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }}
        />
        <Area
          type="monotone"
          dataKey="multiplier"
          stroke="#0d9488"
          strokeWidth={2}
          fill="url(#rampFill)"
          dot={false}
          activeDot={{ r: 3, fill: '#0d9488' }}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}
